import { useMemo, useRef } from 'react';
import { Basis } from '~/models/studio';
import {
  waveformArea,
  waveformBounds,
  waveformDuration,
  waveformsPoints
} from '~/systems/experiment';
import { formatPiFraction } from '~/util';
import {
  amplitudeBounds,
  detuningBounds,
  divisionPoints,
  shapeFromPoints
} from '~/util/displayGrids';
import { useDOMNodeSize } from '~/util/hooks';
import type { Waveform } from '~/models/studio';
import './WaveformPreview.css';

interface WaveformPreviewProps {
  waveform: Waveform;
  isDetuning: boolean;
  basis: Basis;
}

export const WaveformPreview = ({
  waveform,
  isDetuning,
  basis
}: WaveformPreviewProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const rootSize = useDOMNodeSize(rootRef);

  const duration = waveformDuration(waveform);

  const svgSize = {
    width: Math.max(0, rootSize.width),
    height: Math.max(0, rootSize.height)
  };

  const visualBounds = useMemo(
    () => ({ min: svgSize.height, max: 0 }),
    [svgSize.height]
  );

  const valueBounds = useMemo(() => {
    const base = waveformBounds(waveform);
    return isDetuning
      ? detuningBounds(base, visualBounds)
      : amplitudeBounds(base, visualBounds);
  }, [waveform, isDetuning, visualBounds]);

  const timeScale = duration === 0 ? 1 : duration / Math.max(1, svgSize.width);

  const yTicks = useMemo(
    () => divisionPoints(valueBounds, visualBounds),
    [valueBounds, visualBounds]
  );

  const xTickPath = useMemo(() => {
    const nsPerPixel = timeScale;
    const totalNs = duration;
    const height = svgSize.height;
    const step = 100 * Math.pow(2, Math.round(Math.log2(timeScale)));
    let tickPath = '';
    for (let timeNs = 0; timeNs <= totalNs; timeNs += step) {
      tickPath += `M${Math.round(timeNs) / nsPerPixel} 0 v${height}`;
    }
    for (let timeNs = step / 2; timeNs <= totalNs; timeNs += step) {
      tickPath += `M${Math.round(timeNs) / nsPerPixel} 0 v${height}`;
    }
    return tickPath;
  }, [timeScale, duration, svgSize.height]);

  const totalTickPath = useMemo(() => {
    let tickPath = xTickPath;
    const width = svgSize.width;
    for (const tick of yTicks) {
      tickPath += `M0 ${tick.pos} h${width}`;
    }
    return tickPath;
  }, [xTickPath, yTicks, svgSize.width]);

  const waveShape = useMemo(
    () =>
      shapeFromPoints(waveformsPoints([waveform]), {
        unitBounds: valueBounds,
        drawBounds: visualBounds,
        timeScale,
        duration
      }),
    [waveform, valueBounds, visualBounds, timeScale, duration]
  );

  const area = waveformArea(waveform);

  const rootClass = [
    'WaveformPreview',
    isDetuning ? 'WaveformPreview--detuning' : 'WaveformPreview--amplitude',
    basis === Basis.Digital
      ? 'WaveformPreview--raman'
      : basis === Basis.XY
        ? 'WaveformPreview--microwave'
        : 'WaveformPreview--rydberg'
  ].join(' ');

  return (
    <div ref={rootRef} className={rootClass}>
      <svg
        className="WaveformPreview-svg"
        width={svgSize.width}
        height={svgSize.height}
      >
        <rect
          className="WaveformPreview-bg"
          x={0}
          y={0}
          width={svgSize.width}
          height={svgSize.height}
        />
        <path className="WaveformPreview-ticks" d={totalTickPath} />
        <path className="WaveformPreview-waveShape" d={waveShape.shape} />
        <path
          className="WaveformPreview-waveZero"
          d={`M0,${Math.floor(waveShape.yZero)} H${svgSize.width}`}
        />
        <g className="WaveformPreview-area">
          <rect
            x={svgSize.width / 2 - 31}
            y={svgSize.height / 2 - 7}
            width={62}
            height={14}
            rx={2}
          />
          <text
            x={svgSize.width / 2}
            y={svgSize.height / 2}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {formatPiFraction(area)}
          </text>
        </g>
      </svg>
    </div>
  );
};
